import Icon from '../Icon/Icon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CommonButton from '../../components/Buttons/CommonButton';
import CampaignCard from '../../components/Cards/CampaignCard';
import Loading from '../../components/Loading/Loading';
import SearchInput from '../../components/Search/Search';
import { useAuth } from '../../containers/UseAuth';
import { useService } from '../../containers/UseService';
import { getScreensByExternalIds } from '../../services/screensService';
import statsService from '../../services/statsService';
import { campaignsActions, dashboardActions } from '../../store';

const collectCampaignScreenIds = (campaigns = []) =>
    Array.from(
        new Set(
            campaigns.flatMap((campaign) =>
                Array.isArray(campaign?.screen) ? campaign.screen : []
            )
        )
    );

const CampaignList = (props) => {
    const { containerClassName = '', status, dashboard } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const service = useService();
    const [isLoadingBySearch, setIsLoadingBySearch] = useState(false);
    const [campaignsBySearch, setCampaignsBySearch] = useState([]);
    const [isSearch, setIsSearch] = useState(false);
    const campaigns = useSelector((state) => state.campaigns);
    const statuses = campaigns.statuses ? campaigns.statuses : {};
    const current = campaigns.campaignsShowing || {
        status: '',
        page: 1,
        campaigns: [],
    };
    const currentCampaigns = Array.isArray(current?.campaigns)
        ? current.campaigns
        : [];
    const currentStatus = current?.status || '';
    const currentPage = Number(current?.page) || 1;
    const currentStatusTotal = Number(statuses?.[currentStatus] || 0);

    const statsByCampaignId = useMemo(() => {
        const statsMap = {};
        const source = dashboard?.reportsForActiveCampaigns || {};

        Object.values(source).forEach((items = []) => {
            items.forEach((item) => {
                if (!item) return;
                const key = String(
                    item.campaignid ??
                        item.campaignId ??
                        item.id ??
                        item.campaign_id ??
                        ''
                );
                if (key) {
                    statsMap[key] = item;
                }
            });
        });

        return statsMap;
    }, [dashboard?.reportsForActiveCampaigns]);
    const hasMoreCampaigns =
        currentPage <
            Math.ceil(
                currentStatusTotal / process.env.REACT_APP_CAMPAIGNS_PER_PAGE
            ) || currentCampaigns.length === 0;
    const isLoading = campaigns.isLoading;
    const [isCardsExpanded, setIsCardsExpanded] = useState(false);
    const [initialFetch, setInitialFetch] = useState({});
    const [isFetching, setIsFetching] = useState(false);
    const [isCloned, setIsCloned] = useState(false);
    const debounceRef = useRef(setTimeout(() => {}, 1000));
    const [campaignStatus, setCampaignStatus] = useState(null);
    const auth = useAuth();
    const session = auth.session;

    const [searchValue, setSearchValue] = useState('');
    const [userScreens, setUserScreens] = useState(() => new Map());
    const requestedScreenIdsRef = useRef(new Set());

    useEffect(() => {
        if (
            (currentCampaigns.length > 0 && status === 'ACTIVE') ||
            status === 'INACTIVE'
        ) {
            const campaignsArr = isSearch
                ? campaignsBySearch.map((campaign) => campaign.id)
                : currentCampaigns.map((campaign) => campaign.id);
            const allCampaigns = campaignsArr.join(',');
            if (allCampaigns) {
                statsService
                    .getCampaignsScreensStatus(allCampaigns)
                    .then((res) => {
                        setCampaignStatus(res.message);
                    });
            }
        } else {
            setCampaignStatus(null);
        }
    }, [currentCampaigns, status, isSearch, campaignsBySearch]);

    const getDataForActiveCampaigns = async () => {
        let statsByCampaignsHistory =
            await statsService.getStatsOfCampaignsByUserIdHistory(
                session.user.id
            );

        //set acumulated data for actives campaigns
        dispatch(
            dashboardActions.setReportsForActiveCampaigns(
                statsByCampaignsHistory?.data?.stats
            )
        );
    };

    useEffect(() => {
        if (session?.user?.id) {
            getDataForActiveCampaigns();
        }
    }, [session]);

    const endMessage = (
        <p style={{ textAlign: 'center' }}>
            <b>{t('sections.campaign-list.no-more-campaigns')}</b>
        </p>
    );

    const campaignsNotFound = (
        <p style={{ textAlign: 'center' }} className="my-10">
            <b>{t('sections.campaign-list.no-found-campaigns')}</b>
        </p>
    );

    useEffect(
        () => dispatch(campaignsActions.setCurrentStatus(status)),
        [status]
    );

    const toggleExpandAllCards = () => setIsCardsExpanded(!isCardsExpanded);

    const formatCampaignStatus = (status) =>
        status !== ''
            ? t('sections.campaign-list.' + status + '-campaigns')
            : '';

    useEffect(() => {
        // if we are having less than 5 campaigns then it means our list is trucated so we refresh it
        if (
            currentCampaigns.length < 5 &&
            currentCampaigns.length > 0 &&
            hasMoreCampaigns
        ) {
            fetchData();
        }
        fetch();
    }, [currentCampaigns, currentStatus, statuses, hasMoreCampaigns]);

    const intervalRefList = useRef(null);
    const intervalRefListBySearch = useRef(null);
    //creates an interval that keeps the campaign's data up to date
    useEffect(() => {
        if (
            currentCampaigns.length &&
            !campaignsBySearch.length &&
            !isSearch
        ) {
            clearInterval(intervalRefListBySearch.current);
            clearInterval(intervalRefList.current);

            intervalRefList.current = setInterval(() => {
                fetchData(currentCampaigns.length);
            }, 110000);
        } else if (campaignsBySearch.length || isSearch) {
            clearInterval(intervalRefList.current);
        }

        return () => {
            clearInterval(intervalRefList.current);
        };
    }, [currentCampaigns, statuses, campaignsBySearch, isSearch]);

    useEffect(() => {
        return () => {
            clearInterval(intervalRefListBySearch.current);
        };
    }, []);

    const fetchData = async (limit) => {
        const page = 1;
        try {
            const result = await service.fetchMoreDataOfCurrentCampaignList(
                page,
                limit
            );
        } catch (err) {
            console.error('fetchData CATCH error:', err);
        } finally {
            dispatch(campaignsActions.setCurrentStatus(status));
        }
    };

    const fetch = async () => {
        if (
            Object.keys(statuses).length > 0 &&
            currentStatus !== '' &&
            currentStatus === status
        ) {
            if (
                currentCampaigns.length === 0 &&
                hasMoreCampaigns &&
                initialFetch[status] === undefined
            ) {
                const newInitialFetch = Object.assign({}, initialFetch);
                newInitialFetch[status] = true;
                setInitialFetch(newInitialFetch);
                fetchData();
            }
        }

        if (!isCloned) {
            if (
                currentCampaigns.length % 5 !== 0 &&
                hasMoreCampaigns &&
                isFetching
            ) {
                fetchData();
                service.onClickNextPageOfCampaignsList(currentPage);
                //hacer que al hacer click se resetee el time del intervalo que updatea
                setIsFetching(false);
            }
        }
    };

    const fetchBySearch = async (value) => {
        const page = 1;
        const limit = 5;
        setIsLoadingBySearch(true);
        const campaignsBySearch = await service.getCampaigns(
            status,
            page,
            10,
            value
        );
        setIsLoadingBySearch(false);
        return campaignsBySearch;
    };

    const onSearch = async (value) => {
        setSearchValue(value);

        if (debounceRef.current) {
            clearInterval(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            if (value.trim() !== '') {
                clearInterval(intervalRefList.current);
                clearInterval(intervalRefListBySearch.current);

                if (!isSearch) setIsSearch(true);
                const campaignsBySearchFetched = await fetchBySearch(value);
                // FIXME busqueda no funciona con caracteres especiales
                if (campaignsBySearchFetched.length) {
                    setCampaignsBySearch(campaignsBySearchFetched);
                    //create interval for keeping campaignsBySearch up to date
                    intervalRefListBySearch.current = setInterval(async () => {
                        const campaignsBySearchFetched =
                            await fetchBySearch(value);
                        if (campaignsBySearchFetched) {
                            setCampaignsBySearch(campaignsBySearchFetched);
                        }
                    }, 110000);
                } else {
                    setCampaignsBySearch([]);
                }
            } else {
                setIsSearch(false);
                // para que no se quede es estado anterior
                // TODO: mejorar arquitectura de la busqueda
                setCampaignsBySearch([]);
            }
        }, 400);
    };

    useEffect(() => {
        onSearch(searchValue);
    }, [status]);

    useEffect(() => {
        const visibleCampaigns = isSearch
            ? campaignsBySearch
            : current.campaigns;
        const visibleScreenIds = collectCampaignScreenIds(visibleCampaigns);

        if (!visibleScreenIds.length) return;

        let cancelled = false;
        // Guardamos referencia a los IDs que ESTE ciclo del efecto pidió,
        // para poder liberarlos del ref global si el efecto se cancela
        // antes de que la respuesta llegue a aplicarse.
        let idsRequestedByThisRun = [];

        setUserScreens((prevScreens) => {
            const missingScreenIds = visibleScreenIds.filter(
                (screenId) =>
                    !prevScreens.has(screenId) &&
                    !requestedScreenIdsRef.current.has(screenId)
            );

            if (missingScreenIds.length) {
                idsRequestedByThisRun = missingScreenIds;
                missingScreenIds.forEach((screenId) =>
                    requestedScreenIdsRef.current.add(screenId)
                );

                getScreensByExternalIds(missingScreenIds)
                    .then((screens) => {
                        if (cancelled) {
                            // El efecto fue cancelado (re-disparado) antes de
                            // que esta respuesta se aplicara. Liberamos estos
                            // IDs del ref para que el ciclo vigente los vuelva
                            // a pedir; si no, quedan "fantasma" para siempre.
                            idsRequestedByThisRun.forEach((screenId) =>
                                requestedScreenIdsRef.current.delete(screenId)
                            );
                            return;
                        }

                        const fetchedIds = new Set();
                        screens.forEach((screen) => {
                            if (screen?.external_id)
                                fetchedIds.add(screen.external_id);
                            if (screen?.id) fetchedIds.add(screen.id);
                        });

                        // IDs pedidos pero que el backend no devolvió: liberar
                        // para reintentar más adelante en vez de bloquear para siempre.
                        missingScreenIds.forEach((screenId) => {
                            if (!fetchedIds.has(screenId)) {
                                requestedScreenIdsRef.current.delete(screenId);
                            }
                        });

                        if (!screens.length) return;

                        setUserScreens((prevScreens2) => {
                            const nextScreens = new Map(prevScreens2);
                            screens.forEach((screen) => {
                                if (screen?.external_id) {
                                    nextScreens.set(screen.external_id, screen);
                                }
                                if (screen?.id) {
                                    nextScreens.set(screen.id, screen);
                                }
                            });
                            return nextScreens;
                        });
                    })
                    .catch((error) => {
                        console.error('getScreensByExternalIds failed:', error);
                        missingScreenIds.forEach((screenId) =>
                            requestedScreenIdsRef.current.delete(screenId)
                        );
                    });
            }

            return prevScreens;
        });

        return () => {
            cancelled = true;
        };
    }, [campaignsBySearch, current.campaigns, isSearch]);

    return (
        <div className={containerClassName}>
            <Loading isLoading={isLoading || isLoadingBySearch} />
            <div className="font-extralight text-sm md:text-xl pl-2 mb-2">
                {formatCampaignStatus(currentStatus)}{' '}
                <span className="badge white -mt-2">
                    {currentStatusTotal}
                </span>
            </div>
            <div className="flex justify-between content-center mb-2 px-2">
                <div className="flex flex-wrap flex-end content-center">
                    <div
                        onClick={toggleExpandAllCards}
                        className={`rounded antialiased bg-white border h-8 px-2 py-1 text-xs cursor-pointer uppercase transition-colors duration-75 ${
                            isCardsExpanded
                                ? 'bg-blue-400 text-white'
                                : 'hover:bg-blue-400 hover:text-white'
                        }`}>
                        <Icon
                            name="sort"
                            className="mt-1"
                            fixedWidth
                        />
                    </div>
                </div>
                <SearchInput
                    onSearch={onSearch}
                    placeholder={t(
                        'sections.campaign-list.brand-name-or-agency'
                    )}
                />
            </div>
            <div className="block w-full">
                {campaignsBySearch?.length > 0 || isSearch
                    ? campaignsBySearch.map((campaign, index) => (
                          <CampaignCard
                              key={campaign.id}
                              campaign={campaign}
                              isCardExpanded={isCardsExpanded}
                              setIsFetching={setIsFetching}
                              setIsCloned={setIsCloned}
                              status={status}
                              searchRefresh={onSearch}
                              screenStatus={
                                  campaignStatus ? campaignStatus[index] : {}
                              }
                              dashboardFromHome={dashboard}
                              statsByCampaignId={statsByCampaignId}
                              userScreens={userScreens}
                          />
                      ))
                    : currentCampaigns.map((campaign, index) => (
                          <CampaignCard
                              key={campaign.id}
                              campaign={campaign}
                              isCardExpanded={isCardsExpanded}
                              setIsFetching={setIsFetching}
                              setIsCloned={setIsCloned}
                              status={status}
                              screenStatus={
                                  campaignStatus ? campaignStatus[index] : {}
                              }
                              dashboardFromHome={dashboard}
                              statsByCampaignId={statsByCampaignId}
                              userScreens={userScreens}
                          />
                      ))}
                {!isSearch && currentCampaigns.length > 0 && (
                    <div className="flex items-center justify-between mt-4">
                        <CommonButton
                            styleType={'primary'}
                            disabled={currentPage === 1 || isLoading}
                            useLoader={true}
                            onClick={service.onClickPreviusPageOfCampaignsList}
                            label={t('common.words.previous')}
                        />
                        <div className="text-center text-sm font-bold">
                            <span>
                                {currentPage} / {'  '}
                                {isNaN(
                                    Math.ceil(
                                        currentStatusTotal /
                                            process.env
                                                .REACT_APP_CAMPAIGNS_PER_PAGE
                                    )
                                )
                                    ? 1
                                    : Math.ceil(
                                          currentStatusTotal /
                                              process.env
                                                  .REACT_APP_CAMPAIGNS_PER_PAGE
                                      )}
                            </span>
                        </div>
                        {hasMoreCampaigns ? (
                            <CommonButton
                                styleType={'primary'}
                                useLoader={true}
                                disabled={isLoading}
                                onClick={service.onClickNextPageOfCampaignsList}
                                label={t('common.words.next')}
                            />
                        ) : (
                            endMessage
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignList;
